// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.
import { createSelector } from '@reduxjs/toolkit';
import {
  buildEdgesForNode,
  buildEquipmentTree,
  getEquipmentHandles,
  getProtocolsByDirection,
  resolveTopLevelNode,
} from './equipmentUtils';

import { selectStudios, selectStudioUuid } from '../studio/studioSelector';

import {
  PTZ_CAMERA_EQUIPMENT_TYPE_ID,
  ELEVATION_UNIT_EQUIPMENT_TYPE_ID,
  DUAL_STACK_ELEVATION_UNIT_EQUIPMENT_TYPE_ID,
  MORAIL_CARRIAGE_EQUIPMENT_TYPE_ID,
  CAMERAS_GROUP_TYPE_ID,
  STREAM_MONITOR_EQUIPMENT_TYPE_ID,
} from '../../utils/sceneEquipmentsConfig';

/* ================= BASE ================= */

const selectEquipmentState = state => state.equipment;

// get the selected equipment group to be able to display in the right panel
export const selectEquipmentGroups = createSelector(
  [selectEquipmentState],
  state => state.equipmentGroups
);

// get the equipment group type array
export const selectEquipmentGroupTypes = createSelector(
  [selectEquipmentState],
  state => state.equipmentGroupTypes
);

// get the top level equipment group where all nested equipment and equipment groups are nested
export const selectFacilityEquipmentGroup = createSelector(
  [selectEquipmentGroups, selectStudioUuid],
  (groups, studioUuid) => {
    if (!studioUuid) {
      return false;
    }

    return groups.find(group => group.name === studioUuid) ?? null;
  }
);

// get the selected equipment to be able to display its properties and settings in the right panel
export const selectSelectedEquipment = createSelector(
  [selectEquipmentState],
  state => state.selectedEquipment
);

// get the selected equipment group to be able to display in the right panel
export const selectSelectedEquipmentGroup = createSelector(
  [selectEquipmentState],
  state => state.selectedEquipmentGroup
);

// get the selected equipment group group equipment
export const selectSelectedEquipmentGroupEquipment = createSelector(
  [selectSelectedEquipmentGroup],
  selectedGroup => selectedGroup?.equipment ?? []
);

// get the camera control group
export const selectCameraControlGroup = createSelector(
  [selectEquipmentState],
  state => state.cameraControlGroup
);

// get the camera control group equipment
export const selectSelectedCameraGroupEquipment = createSelector(
  [selectCameraControlGroup],
  cameraControlGroup => cameraControlGroup?.equipment ?? []
);

// get presets list
export const selectPresets = createSelector([selectEquipmentState], state => state.presets);

// get preset sequences list
export const selectPresetSequences = createSelector(
  [selectEquipmentState],
  state => state.presetSequences
);

// get the selected preset sequence
export const selectSelectedPresetSequence = createSelector(
  [selectEquipmentState],
  state => state.selectedPresetSequence
);

// get the selected equipment uuid (co_equip_uuid) to send to websocket action
export const selectSelectedEquipmentUuid = createSelector(
  [selectEquipmentState],
  state => state.selectedEquipment?.co_equip_uuid
);

// get preset manager
// TODO : refactor should check equipment type instead of uuid of the equipment to be able to support multiple preset manager equipment
const PRESET_MANAGER_UUID = '36f0754c-8013-4d36-ae10-a8c23dcb2bd7';
export const selectPresetManager = createSelector([selectFacilityEquipmentGroup], group =>
  group?.equipment?.find(e => e.co_equip_uuid === PRESET_MANAGER_UUID)
);

export const selectPresetManagerState = createSelector([selectPresetManager], presetManager => {
  const state = presetManager?.current_config?.properties?.state || {};
  return Object.keys(state)
    .filter(key => key.startsWith('ptz'))
    .reduce((obj, key) => {
      obj[key] = state[key];
      return obj;
    }, {});
});

// get the studio uuid by equipment uuid to be able to send websocket action
export const selectStudioUUIDByEquipmentUUID = (state, equipmentUUID) => {
  const studios = selectStudios(state);

  if (!equipmentUUID || !studios?.length) {
    return undefined;
  }

  const studio = studios.find(studio =>
    studio.studio_equipment?.some(studioEquip => studioEquip.co_equip_uuid === equipmentUUID)
  );

  return studio?.studio_uuid;
};

// parse the equipment mappings table to find the first parent group of the selected equipment
export const selectSelectedEquipmentParentGroup = createSelector(
  [
    selectSelectedEquipment,
    selectEquipmentGroups,
    (_, directMemberOnly = false) => directMemberOnly,
  ],
  (selectedEquipment, groups, directMemberOnly) =>
    groups.find(g =>
      g?.equipment_group_mappings?.find(e => {
        const matches = e?.company_equipment?.co_equip_uuid === selectedEquipment?.co_equip_uuid;

        if (!matches) {
          return false;
        }

        return directMemberOnly ? e?.direct_member === true : true;
      })
    ) ?? null
);

// get the children equipment group of the selected equipment group
export const selectSelectedEquipmentGroupWithChildren = createSelector(
  [selectSelectedEquipmentGroup, selectEquipmentGroups],
  (group, groups = []) => {
    if (!group) {
      return null;
    }

    const children = groups.filter(g => g.parent_uuid === group.equip_group_uuid);

    return {
      ...group,
      children,
    };
  }
);

// get all equipment and children equipment group of the selected equipment group
export const selectSelectedEquipmentGroupChildren = createSelector(
  [selectSelectedEquipmentGroupWithChildren],
  group => {
    if (!group) {
      return [];
    }

    const directEquipment = group.equipment?.filter(eq => eq.direct_member) ?? [];

    return [...directEquipment, ...(group.children ?? [])];
  }
);

// get equipement inside groups to be able to build edges between equipment inside groups and other equipment or groups
export const selectAllEquipmentInsideGroups = createSelector(
  [selectEquipmentGroups],
  (groups = []) => {
    const groupedEquipment = groups.flatMap(group => group.equipment ?? []);
    const map = new Map();

    [...groupedEquipment].forEach(eq => {
      if (eq?.co_equip_uuid) {
        map.set(eq.co_equip_uuid, eq);
      }
    });

    return Array.from(map.values());
  }
);

// get the ancestry of the group as a list of groups
export const selectSelectedCameraControlGroupAncestry = createSelector(
  [selectCameraControlGroup, selectEquipmentGroups, selectFacilityEquipmentGroup],
  (cameraControlGroup, groups, facilityEquipmentGroup) => {
    if (!cameraControlGroup || !groups?.length) {
      return [];
    }

    const getParent = group =>
      groups.find(g => group?.parent_uuid && group?.parent_uuid === g?.equip_group_uuid);

    const buildAncestry = group => {
      const parent = getParent(group);

      if (!parent) {
        return [group];
      }

      return [...buildAncestry(parent), group];
    };

    const ancestry = buildAncestry(cameraControlGroup);

    return ancestry.filter(
      group => group?.equip_group_uuid !== facilityEquipmentGroup?.equip_group_uuid
    );
  }
);

// get all edges(conection lines) between equipment and groups based on the handles of the equipment and equipment groups
export const selectAllEdges = createSelector(
  [
    selectAllEquipmentInsideGroups,
    selectEquipmentGroups,
    selectFacilityEquipmentGroup,
    (_, nodes) => nodes,
  ],
  (equipmentUsed = [], groups = [], facilityEquipmentGroup, nodes = []) => {
    const facilityEquipmentGroupUuid = facilityEquipmentGroup?.equip_group_uuid;
    const equipmentEdges = equipmentUsed.flatMap(eq =>
      buildEdgesForNode({
        nodeId: eq.co_equip_uuid,
        equipment: equipmentUsed,
        groups,
        nodes,
        facilityEquipmentGroupUuid,
      })
    );

    const edgeMap = new Map();
    // Edges are built per real equipment port, so a group pair connected in both directions
    // produces two edges (source/target swapped) that visually overlap. Collapse those into one
    // edge with an arrowhead on each end instead of drawing two overlapping single-headed edges.
    const groupBoundaryEdgeByPair = new Map();

    equipmentEdges.forEach(edge => {
      if (edge.data?.isGroupBoundary) {
        const pairKey = [edge.source, edge.target].sort().join('|');
        const existing = groupBoundaryEdgeByPair.get(pairKey);

        if (existing) {
          const isReverse = edge.source === existing.target && edge.target === existing.source;

          if (isReverse && !existing.markerStart) {
            const bidirectional = {
              ...existing,
              markerStart: { type: 'arrowclosed', color: existing.data.color },
              label:
                typeof existing.label === 'string'
                  ? existing.label.replace(' → ', ' ↔ ')
                  : existing.label,
            };
            groupBoundaryEdgeByPair.set(pairKey, bidirectional);
            edgeMap.set(bidirectional.id, bidirectional);
          }
          return;
        }

        groupBoundaryEdgeByPair.set(pairKey, edge);
      }
      edgeMap.set(edge.id, edge);
    });

    return Array.from(edgeMap.values());
  }
);

// get the selected equipment connections
export const selectEquipmentConnections = createSelector(
  [selectSelectedEquipment, selectAllEquipmentInsideGroups],
  (equipment, equipmentList) => {
    const handles = getEquipmentHandles(equipment, equipmentList).filter(h => !h.decorative);

    const inputs = handles.filter(h => h.connectionType === 'input');
    const outputs = handles.filter(h => h.connectionType === 'output');
    return { inputs, outputs };
  }
);

// get the equipment group details panel's connection rows and connectable options for the selected group.
export const selectGroupConnections = createSelector(
  [selectSelectedEquipmentGroup, (_, nodes) => nodes],
  (group, nodes = []) => {
    const nodeId = group?.equip_group_uuid;
    const groupNode = nodes.find(n => n.id === nodeId);
    const handles = groupNode?.data?.handles || {};
    const allHandles = [...(handles.inputs || []), ...(handles.outputs || [])];

    // One row per connected counterpart (group or equipment) — mirrors the canvas handles 1:1,
    // instead of collapsing everything into a single Inputs/Outputs value.
    const connectedRows = allHandles
      .filter(h => h.decorative && (h.connection?.sourceNodeId || h.connection?.targetNodeId))
      .map(h => {
        const counterpartId = h.connection.sourceNodeId || h.connection.targetNodeId;
        const counterpartNode = nodes.find(n => n.id === counterpartId);
        const direction =
          h.connection.sourceNodeId && h.connection.targetNodeId
            ? 'both'
            : h.connection.sourceNodeId
              ? 'input'
              : 'output';

        return {
          id: h.id,
          counterpartId,
          counterpartType: counterpartNode?.type === 'equipmentGroup' ? 'group' : 'equipment',
          counterpartName:
            h.sourceName || h.targetName || counterpartNode?.data?.state?.name || counterpartId,
          direction,
          signalCount: h.children?.length || 0,
          tooltipText: h.tooltipText,
          children: h.children || [],
        };
      });

    // The merged placeholder handle for whatever's still unconnected — its children carry the
    // protocols we can still match against other nodes to start a new connection.
    const openPlaceholder = allHandles.find(
      h =>
        h.decorative &&
        !h.connection?.sourceNodeId &&
        !h.connection?.targetNodeId &&
        h.children?.length
    );

    const protocolsNeedingOutput = getProtocolsByDirection(openPlaceholder?.children, 'input');
    const protocolsNeedingInput = getProtocolsByDirection(openPlaceholder?.children, 'output');

    const hasValidConnections = candidateHandles =>
      [...getProtocolsByDirection(candidateHandles, 'output')].some(p =>
        protocolsNeedingOutput.has(p)
      ) ||
      [...getProtocolsByDirection(candidateHandles, 'input')].some(p =>
        protocolsNeedingInput.has(p)
      );

    const addOptions =
      protocolsNeedingOutput.size || protocolsNeedingInput.size
        ? nodes
            .filter(
              n =>
                n.type === 'equipmentGroup' &&
                n.id !== nodeId &&
                resolveTopLevelNode(n.id, nodes)?.id !== nodeId &&
                hasValidConnections([
                  ...(n.data?.handles?.inputs || []),
                  ...(n.data?.handles?.outputs || []),
                ])
            )
            .map(node => {
              const parentName = node.parentId
                ? nodes.find(n => n.id === node.parentId)?.data?.state?.name
                : null;
              const label = parentName
                ? `${node.data?.state?.name || node.id} (${parentName})`
                : node.data?.state?.name || node.id;

              return { label, value: `group__${node.id}__`, isSubgroup: Boolean(node.parentId) };
            })
        : [];

    // Top-level groups first, subgroups after — within each bucket, alphabetical by label so
    // same-named groups land next to their disambiguated siblings.
    addOptions.sort((a, b) => {
      if (a.isSubgroup !== b.isSubgroup) {
        return a.isSubgroup ? 1 : -1;
      }
      return a.label.localeCompare(b.label);
    });

    return { connectedRows, addOptions: addOptions.map(({ isSubgroup, ...option }) => option) };
  }
);

// get the selected equipment setting for the equipment details
export const selectEquipmentSettings = createSelector(
  [state => state.equipment.selectedEquipment?.current_config?.settings],
  settings => {
    const tree = buildEquipmentTree(settings, 'setting');
    return (
      tree?.filter(
        n =>
          ![
            'sources',
            'destinations',
            'button_config',
            'joystick_config',
            'active_destination',
            'action_button',
          ].includes(n.name)
      ) || []
    );
  }
);

// get the selected equipment properties for the equipment details
export const selectEquipmentProperties = createSelector(
  [state => state.equipment.selectedEquipment?.current_config?.properties],
  properties => {
    const tree = buildEquipmentTree(properties, 'property');
    return (
      tree?.filter(
        n => !['streams', 'inputs', 'outputs', 'button_states', 'actions'].includes(n.name)
      ) || []
    );
  }
);

export const selectEquipmentActions = createSelector(
  [state => state.equipment.selectedEquipment?.current_config?.properties],
  properties => {
    const tree = buildEquipmentTree(properties, 'property');
    return (
      tree
        ?.filter(n => n.name === 'actions')?.[0]
        ?.options.filter(
          // Ignore admin actions
          n => !['update_connection', 'confirm_connection', 'press_button'].includes(n.value)
        ) || []
    );
  }
);

export const selectEquipmentToggles = createSelector(
  [state => state.equipment.selectedEquipment?.current_config?.settings],
  settings => {
    const tree = buildEquipmentTree(settings, 'setting');
    const fields = tree?.filter(n => n.name === 'destinations')?.[0]?.children ?? [];
    const toggles = [];
    fields?.forEach(f => {
      const options = f?.children?.filter(n => n.name === 'ctrl_settings')?.[0]?.children;
      options?.forEach(o => {
        const toggle = { value: o.name, label: o.label };
        if (!toggles.find(t => t.value === toggle.value)) {
          toggles.push(toggle);
        }
      });
    });
    return toggles;
  }
);

export const selectEquipmentButtonConfig = createSelector(
  [state => state.equipment.selectedEquipment?.current_config?.settings],
  settings => {
    const tree = buildEquipmentTree(settings, 'setting');
    return (
      tree
        ?.filter(n => n.name === 'button_config')?.[0]
        ?.children.filter(
          // Ignore brightnesses
          b => !['active_brightness', 'inactive_brightness'].includes(b.name)
        ) || []
    );
  }
);

// get the selected equipment status
export const selectedEquipmentStatus = createSelector(
  [selectSelectedEquipment],
  equipment => equipment?.current_config?.properties?.state?.status?.eprop_val_uuid?.value
);

// get the selected equipment group status based on the status of its nested equipment, if any of the nested equipment
export const selectedEquipmentGroupStatus = createSelector(
  [selectSelectedEquipmentGroupWithChildren],
  equipmentGroup => {
    const equipment = equipmentGroup?.equipment ?? [];

    if (!equipment.length) {
      return null;
    }
    // Equipment without a status value resolves to undefined; .filter(Boolean) removes those
    // entries so only equipment that actively reports a status contributes to the aggregation.
    // should equipment without a status be considered "error" or "ok"?
    const statuses = equipment
      .map(eq => eq?.current_config?.properties?.state?.status?.eprop_val_uuid?.value)
      .filter(Boolean);

    // if no equipment has a status, return null
    if (!statuses.length) {
      return null;
    }
    // return error if ANY are in error, warning if ANY are in warning, otherwise ok if ALL are ok
    if (statuses.includes('error')) {
      return 'error';
    }

    if (statuses.includes('warning')) {
      return 'warning';
    }

    // only return ok if ALL are ok
    const allOk = statuses.every(s => s === 'ok');

    return allOk ? 'ok' : null;
  }
);

// get the ptz camera equipment inside the control group
export const selectedPTZ = createSelector(
  [selectCameraControlGroup],
  group =>
    group?.equipment?.find(
      eq => eq.equip_type_uuid === PTZ_CAMERA_EQUIPMENT_TYPE_ID && eq.direct_member === true
    ) || null
);

// get the elevation unit equipment inside the control group
export const selectedElevationUnit = createSelector(
  [selectCameraControlGroup, selectEquipmentGroups],
  (group, equipmentGroups) =>
    group?.equipment?.find(
      eq =>
        eq.equip_type_uuid === ELEVATION_UNIT_EQUIPMENT_TYPE_ID ||
        eq.equip_type_uuid === DUAL_STACK_ELEVATION_UNIT_EQUIPMENT_TYPE_ID
    ) ??
    equipmentGroups
      .find(g => g.equip_group_uuid === group.parent_uuid)
      ?.equipment?.find(
        eq =>
          (eq?.equip_type_uuid === ELEVATION_UNIT_EQUIPMENT_TYPE_ID ||
            eq?.equip_type_uuid === DUAL_STACK_ELEVATION_UNIT_EQUIPMENT_TYPE_ID) &&
          eq?.direct_member === true
      ) ??
    null
);

// true only when the camera control group itself (not its parent) directly contains an elevation unit
export const selectCameraControlGroupHasElevationUnit = createSelector(
  [selectCameraControlGroup],
  group =>
    group?.equipment?.some(
      eq =>
        eq.direct_member === true &&
        (eq.equip_type_uuid === ELEVATION_UNIT_EQUIPMENT_TYPE_ID ||
          eq.equip_type_uuid === DUAL_STACK_ELEVATION_UNIT_EQUIPMENT_TYPE_ID)
    ) ?? false
);

// get the morail carriage equipment inside the control group
export const selectedMorailCarriage = createSelector(
  [selectCameraControlGroup, selectEquipmentGroups],
  (group, equipmentGroups) =>
    group?.equipment?.find(eq => eq.equip_type_uuid === MORAIL_CARRIAGE_EQUIPMENT_TYPE_ID) ??
    equipmentGroups
      .find(g => g.equip_group_uuid === group.parent_uuid)
      ?.equipment?.find(
        eq =>
          eq?.equip_type_uuid === MORAIL_CARRIAGE_EQUIPMENT_TYPE_ID && eq?.direct_member === true
      ) ??
    null
);

// get the equipment inside the camera control group to be able to display them in the camera control group navigation
export const selectCameraControlGroups = createSelector(
  [selectSelectedEquipmentGroupWithChildren, selectEquipmentGroups],
  (equipmentGroup, equipmentGroups) => {
    if (!equipmentGroup) {
      return [];
    }

    const hasPTZ = equip =>
      equip?.equip_type_uuid === PTZ_CAMERA_EQUIPMENT_TYPE_ID && equip?.direct_member === true;

    if (equipmentGroup?.equip_group_type_uuid === CAMERAS_GROUP_TYPE_ID) {
      return equipmentGroup.equipment?.filter(hasPTZ).sort((a, b) => a.name.localeCompare(b.name));
    }

    const collectGroupsWithPTZ = group => {
      const result = [];

      if (group?.equipment?.some(hasPTZ)) {
        result.push(group);
      }

      if (group?.children?.length) {
        const childEquipmentGroups = equipmentGroups.filter(
          g => g.parent_uuid === group.equip_group_uuid
        );

        childEquipmentGroups.forEach(child => {
          result.push(...collectGroupsWithPTZ(child));
        });
      }

      return [...result].sort((a, b) => a.name.localeCompare(b.name));
    };

    return collectGroupsWithPTZ(equipmentGroup);
  }
);

export const selectEquipmentStreams = createSelector(
  [state => state.equipment.selectedEquipment?.current_config],
  config =>
    Object.keys(config?.settings?.sources)
      .filter(v => v.startsWith('stream'))
      .sort()
      .map(
        s =>
          s && {
            name: s,
            uuid: config?.settings?.sources?.[s]?.uuid?.equip_setting_val_uuid?.value,
            properties: config?.properties?.state?.[s],
          }
      )
);

const streamPropertiesFromConfig = config =>
  Object.keys(config?.settings?.sources || {})
    .filter(s => s.startsWith('stream'))
    .sort()
    .map(s => ({
      name: s,
      uuid: config.settings.sources[s]?.uuid?.equip_setting_val_uuid?.value,
      properties: config.properties?.streams?.[s],
    }));

export const selectEquipmentStreamProperties = createSelector(
  [state => state.equipment.selectedEquipment?.current_config],
  config => streamPropertiesFromConfig(config)
);

export const selectStreamMonitorStreamProperties = createSelector(
  [selectFacilityEquipmentGroup],
  facilityGroup => {
    const monitors =
      facilityGroup?.equipment?.filter(
        e => e.equip_type_uuid === STREAM_MONITOR_EQUIPMENT_TYPE_ID
      ) ?? [];
    return monitors.flatMap(sm =>
      sm?.current_config ? streamPropertiesFromConfig(sm.current_config) : []
    );
  }
);

export const selectStreamWebrtcUrlByUuid = createSelector(
  [selectStreamMonitorStreamProperties, (_, uuid) => uuid],
  (streams, uuid) => {
    if (!uuid) {
      return null;
    }
    const stream = streams.find(s => s.uuid === uuid);
    return stream?.properties?.webrtc_source?.url?.eprop_val_uuid?.value ?? null;
  }
);

// get group children by parent id to get the children group object of a group (backend don t return full equipment group object in the group.children array)
export const selectGroupsByParentId = createSelector([selectEquipmentGroups], groups => {
  const map = {};

  if (!groups) {
    return map;
  }

  for (const group of groups) {
    const parentId = group?.parent_uuid;
    if (!parentId) {
      continue;
    }

    if (!map[parentId]) {
      map[parentId] = [];
    }

    map[parentId].push(group);
  }

  return map;
});

// get the partial lock status for a equipmentGroup by its id

export const selectHasPartialLockByGroupId = createSelector(
  [selectGroupsByParentId, (_, groupId) => groupId],
  (byParent, groupId) => {
    if (!groupId) {
      return false;
    }

    const childGroups = byParent[groupId] ?? [];

    const lockedChildGroups = childGroups.filter(group => (group?.lock?.length ?? 0) > 0);
    return {
      hasPartialLock: lockedChildGroups.length > 0,
      lockedChildGroups,
    };
  }
);
